
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { useAuth } from '../../contexts/AuthContext';
import { GiSpiralBottle } from 'react-icons/gi';
import { logger } from '../../utils/logger';
import { supabase } from '../../lib/supabase';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);

  // Limpar qualquer sessão residual ao carregar a página de login
  useEffect(() => {
    const clearOldSession = async () => {
      try {
        logger.debug('[LoginPage] Verificando sessão ativa ao carregar página...');
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          logger.warn('[LoginPage] Sessão ativa detectada, limpando antes de permitir novo login...');
          await supabase.auth.signOut();
          localStorage.removeItem('supabase.auth.token');
          logger.success('[LoginPage] Sessão antiga limpa com sucesso');
        }
      } catch (err) {
        logger.warn('[LoginPage] Erro ao limpar sessão antiga (ignorando):', err);
      }
    };

    void clearOldSession();
  }, []);

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    logger.debug('[LoginPage] Iniciando processo de login...');
    logger.sensitive('[LoginPage] Email:', formData.email);
    
    setLoading(true);
    setError('');
    let hasTimeout = false;
    let loginSuccess = false;

    try {
      // RATE LIMITING: Verificar se o email pode tentar login
      logger.debug('[LoginPage] Verificando rate limiting por email...');
      const { data: canLogin, error: rateLimitError } = await supabase.rpc('check_login_attempts', {
        p_email: formData.email.toLowerCase().trim()
      });

      if (rateLimitError) {
        logger.error('[LoginPage] Erro ao verificar rate limiting:', rateLimitError);
        // Continuar mesmo com erro (fallback gracioso)
      } else if (canLogin === false) {
        logger.warn('[LoginPage] Email bloqueado por excesso de tentativas');
        setError('Muitas tentativas de login falhadas. Por favor, aguarde 15 minutos e tente novamente.');
        setLoading(false);
        return;
      }

      logger.debug('[LoginPage] Rate limiting OK, prosseguindo com login...');
      logger.debug('[LoginPage] Chamando signIn...');
      await signIn(formData.email, formData.password);
      logger.success('[LoginPage] signIn completado com sucesso');
      loginSuccess = true;
      
      // Sucesso - o AuthContext irá redirecionar automaticamente
      logger.debug('[LoginPage] Redirecionando para /dashboard');
      navigate('/dashboard');
    } catch (error: any) {
      logger.error('[LoginPage] Erro no login:', error);
      
      // Se for erro de timeout mas o login pode ter funcionado, tentar redirecionar mesmo assim
      if (error.message?.includes('timeout')) {
        hasTimeout = true;
        logger.warn('[LoginPage] Timeout detectado, mas tentando redirecionar mesmo assim...');
        setTimeout(() => {
          logger.debug('[LoginPage] Redirecionando para /dashboard após timeout');
          navigate('/dashboard');
        }, 1000);
      } else {
        setError(error.message || 'Erro ao fazer login. Verifique suas credenciais.');
        setLoading(false);
      }
    } finally {
      // RATE LIMITING: Registrar tentativa de login (sucesso ou falha)
      try {
        logger.debug('[LoginPage] Registrando tentativa de login...');
        await supabase.rpc('register_login_attempt', {
          p_email: formData.email.toLowerCase().trim(),
          p_success: loginSuccess,
          p_ip: null, // Não temos acesso ao IP real no frontend
          p_user_agent: navigator.userAgent
        });
        logger.debug('[LoginPage] Tentativa de login registrada com sucesso');
      } catch (registerError) {
        logger.warn('[LoginPage] Erro ao registrar tentativa de login (não crítico):', registerError);
        // Ignorar erro de registro (não deve bloquear o fluxo)
      }

      if (!hasTimeout) {
        logger.debug('[LoginPage] Finalizando processo de login');
        setLoading(false);
      }
    }
  };

  const goToSignUp = () => {
    navigate('/signup');
  };

  const goBack = () => {
    navigate('/');
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);

    try {
      await signInWithGoogle();
    } catch (err: any) {
      logger.error('[LoginPage] Erro no login com Google:', err);
      setError(err?.message || 'Não foi possível autenticar com Google.');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 py-8">
      {/* Header com botão voltar */}
      <div className="w-full flex justify-start mb-6">
        <button
          onClick={goBack}
          className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center shadow-soft hover:bg-gray-100 transition-colors border border-gray-200"
        >
          <i className="ri-arrow-left-line text-gray-600 text-xl"></i>
        </button>
      </div>

      {/* Logo e Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-card flex items-center justify-center mb-4 mx-auto shadow-card overflow-hidden">
          <img 
            src="/Imagens/logo 2.0.png"
            alt="Logo Magistral"
            className="w-full h-full object-contain"
          />
        </div>
        <h1 className="text-3xl font-bold text-gray-800 font-sans" style={{ 
          fontWeight: 700,
          textShadow: '1px 2px 4px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.05)'
        }}>
          Magistral
        </h1>
        <p className="text-gray-600 mt-2 font-sans" style={{ fontWeight: 500 }}>
          Bem-vindo de volta!
        </p>
      </div>

      {/* Formulário */}
      <div className="w-full max-w-sm bg-white rounded-card p-6 shadow-card border border-gray-100">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800 font-sans" style={{ fontWeight: 700 }}>
            Entrar na sua conta
          </h2>
          <p className="text-gray-600 text-sm mt-1 font-sans" style={{ fontWeight: 500 }}>
            Digite seus dados para continuar
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
          
          <Input
            label="E-mail"
            type="email"
            placeholder="seu@email.com"
            value={formData.email}
            onChange={handleInputChange('email')}
            required
          />
          
          <Input
            label="Senha"
            type="password"
            placeholder="Sua senha"
            value={formData.password}
            onChange={handleInputChange('password')}
            required
          />

          <div className="text-right">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-green-500 text-sm hover:text-green-600 transition-colors font-sans" style={{ fontWeight: 500 }}
            >
              Esqueceu a senha?
            </button>
          </div>

          <Button type="submit" size="lg" className="w-full mt-6" disabled={loading}>
            {loading ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Entrando...
              </>
            ) : (
              <>
                <i className="ri-login-box-line mr-2"></i>
                Entrar
              </>
            )}
          </Button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-3 bg-white text-gray-500">ou continue com</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full flex items-center justify-center gap-3"
          onClick={handleGoogleSignIn}
          disabled={googleLoading}
        >
          {googleLoading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Conectando...
            </>
          ) : (
            <>
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google"
                className="w-5 h-5"
              />
              Entrar com Google
            </>
          )}
        </Button>

        <div className="text-center mt-6">
          <p className="text-gray-600 text-sm font-sans" style={{ fontWeight: 500 }}>
            Não tem uma conta?{' '}
            <button
              onClick={goToSignUp}
              className="text-green-500 font-medium hover:text-green-600 transition-colors font-sans" style={{ fontWeight: 700 }}
            >
              Criar Conta
            </button>
          </p>
        </div>
      </div>

      {/* Benefícios */}
      <div className="grid grid-cols-3 gap-4 mt-8 w-full max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-2 mx-auto shadow-soft border border-gray-100">
            <i className="ri-shield-check-line text-green-500 text-[1.67rem]"></i>
          </div>
          <p className="text-xs text-gray-600 font-sans" style={{ fontWeight: 500 }}>Seguro</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-2 mx-auto shadow-soft border border-gray-100">
            <div 
              className="w-8 h-8 bg-purple-500"
              style={{
                mask: 'url("/Imagens/familia-estilizada.png") center/contain no-repeat',
                WebkitMask: 'url("/Imagens/familia-estilizada.png") center/contain no-repeat',
              }}
              title="Família estilizada"
            />
          </div>
          <p className="text-xs text-gray-600 font-sans" style={{ fontWeight: 500 }}>Família</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-2 mx-auto shadow-soft border border-gray-100">
            <GiSpiralBottle className="text-green-500 text-[1.67rem]" />
          </div>
          <p className="text-xs text-gray-600 font-sans" style={{ fontWeight: 500 }}>Homepatia</p>
        </div>
      </div>

      {/* Mensagem Motivacional */}
      <div className="text-center mt-6">
        <p className="text-gray-500 text-sm font-sans" style={{ fontWeight: 500 }}>
          ✨ Viva com mais leveza
        </p>
      </div>
    </div>
  );
}
